import { PageHero } from "@/components/site/PageHero";
import { Reveal, Overline } from "@/components/site/Reveal";
import { CTABand } from "@/components/site/CTABand";
import { stats, IMAGES } from "@/data/content";
import { ShieldCheck, Target, HeartHandshake, Clock } from "lucide-react";

const values = [
  { icon: ShieldCheck, title: "Integrity", body: "Vetted, trained and accountable people you can rely on, every shift, every site." },
  { icon: Target, title: "Precision", body: "Clear SLAs, live reporting and measurable outcomes across every service line." },
  { icon: HeartHandshake, title: "Partnership", body: "We act as an extension of your team, not just a supplier on a contract." },
  { icon: Clock, title: "Availability", body: "A 24/7 operations centre means we're always reachable and always responsive." },
];

const chapters = [
  { n: "01", title: "Founded on service", body: "TFS was built to solve a simple frustration: managing multiple facilities suppliers is inefficient, inconsistent and expensive. We bring every service under one roof, with one point of accountability." },
  { n: "02", title: "Built for scale", body: "From a single building to a national portfolio, our people, systems and processes are designed to deliver the same exacting standard everywhere, at any volume." },
  { n: "03", title: "Driven by compliance", body: "Safety and compliance are never an afterthought. Accredited processes, licensed personnel and full documentation keep our clients protected and inspection-ready." },
];

export default function About() {
  return (
    <div>
      <PageHero
        overline="About Us"
        title={["Facilities management,", "reimagined as one", "seamless service."]}
        subtitle="Total Facilities Services is a UK-wide facilities management provider trusted by commercial landlords, residential developers, managing agents and construction firms."
        image={IMAGES.commercial}
      />

      <section className="bg-ink py-20 md:py-28 border-t border-border">
        <div className="max-w-container mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <Reveal>
              <div className="relative aspect-[4/5] overflow-hidden border border-border">
                <img src={IMAGES.maintenance} alt="Maintenance engineer" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:pl-8">
            <Reveal><Overline>Our story</Overline></Reveal>
            <div className="mt-10 divide-y divide-border">
              {chapters.map((c, i) => (
                <Reveal key={c.n} delay={i * 0.05}>
                  <div className="py-8 grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-2 font-display font-black text-5xl text-border leading-none">{c.n}</div>
                    <div className="md:col-span-10">
                      <h3 className="font-display font-medium text-2xl text-white tracking-tight">{c.title}</h3>
                      <p className="mt-3 text-slatetxt text-lg leading-relaxed">{c.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-surface py-20 md:py-28">
        <div className="max-w-container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border border-y border-border">
            {stats.map((s) => (
              <Reveal key={s.label}>
                <div className="py-10 px-4 md:px-8">
                  <div className="font-display font-bold text-4xl md:text-5xl text-white tracking-tight">{s.value}</div>
                  <div className="text-sm text-slatetxt mt-2">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink py-20 md:py-28">
        <div className="max-w-container mx-auto px-6 md:px-12">
          <Reveal><Overline>What we stand for</Overline></Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={(i % 4) * 0.06}>
                  <div className="bg-surface border border-border p-8 h-full hover:border-brand/50 transition-colors duration-300">
                    <Icon size={32} className="text-brand-light" strokeWidth={1.5} />
                    <h3 className="mt-6 font-display font-medium text-xl text-white tracking-tight">{v.title}</h3>
                    <p className="mt-3 text-slatetxt leading-relaxed">{v.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
